import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
// Removed: import Vue from "vue";
import {POIUtility} from "../../../../utils/models/POI"; // Assuming POI enum is exported
import {isClickOnWaypointHelper} from "../../../../utils/helpers/IsClickOnWaypointHelper";
import emitter from "../../../../utils/bus";
import {MapLayerCategory} from "../../../../utils/layer-registry";
import {
  sharedPoiCenter,
  sharedPoiFromUrl,
  sharedPoiSelection,
  sharedPoiZoom,
} from "../../../../utils/helpers/poiSharing";

// Define constants for the single source and layer IDs
const POI_SOURCE_ID = 'poi-source';
const POI_LAYER_ID = 'poi-layer';

// How long to wait for the POI dialog to register `window.showPoi` before giving
// up on a shared-POI link. ~2s total, which is generous for a hook that has
// usually already run.
const SHARED_POI_SHOW_ATTEMPTS = 20;
const SHARED_POI_SHOW_RETRY_MS = 100;

// -- Define the module’s state interface (remains the same)
export interface BaseMapDataPOIsState {
  activatedPoiTypes: string[];
  poiList: POIUtility[];
  trailHighlightsAvailable: boolean; // Assuming this is part of the state, adjust as needed
  // Whether a `?poi_id=` deep link has already been honoured this page load, so
  // re-adding the layers on a style change doesn't reopen a closed sheet.
  sharedPoiHandled: boolean;
}

// -- Define RootState type (replace 'any' with your actual RootState if available)
type RootState = any; // Or import { RootState } from '@/store';

// -- Initialize state (removed Vue.observable)
const state: BaseMapDataPOIsState = {
  // Start with an empty array. We’ll rehydrate in initActivePois.
  activatedPoiTypes: [],
  // Get and sort all POIs (using your POIUtility)
  poiList: POIUtility.allPOIUtils().sort((a, b) => {
    return a.prettyName().localeCompare(b.prettyName());
  }),

  trailHighlightsAvailable: false, // Assuming this is part of the state, adjust as needed

  sharedPoiHandled: false
};

// -- Mutations (remain the same)
const mutations: MutationTree<BaseMapDataPOIsState> = {
  SET_ACTIVATED_POI_TYPES(state, poiTypes: string[]) {
    state.activatedPoiTypes = poiTypes;
    localStorage.setItem("activatedPoiTypes", JSON.stringify(poiTypes));
  },
  ADD_ACTIVATED_POI_TYPE(state, poiType: string) {
    if (!state.activatedPoiTypes.includes(poiType)) {
      // In Vuex 4/Vue 3, direct push is reactive
      state.activatedPoiTypes.push(poiType);
      localStorage.setItem(
        "activatedPoiTypes",
        JSON.stringify(state.activatedPoiTypes)
      );
    }
  },
  REMOVE_ACTIVATED_POI_TYPE(state, poiType: string) {
    state.activatedPoiTypes = state.activatedPoiTypes.filter(
      (type) => type !== poiType
    );
    localStorage.setItem(
      "activatedPoiTypes",
      JSON.stringify(state.activatedPoiTypes)
    );
  },
  REMOTE_ALL_POI_TYPES(state) { // Renamed mutation in original code: REMOVE_ALL_POI_TYPES?
    state.activatedPoiTypes = [];
    localStorage.removeItem("activatedPoiTypes");
  },
  SET_SHARED_POI_HANDLED(state, handled: boolean) {
    state.sharedPoiHandled = handled;
  }
};

// -- Actions (remain the same structure, context types updated)
const actions: ActionTree<BaseMapDataPOIsState, RootState> = {

  /**
   * Adds the single POI source and layer to the map if they don't exist.
   */
  addPoiSourceAndLayer({ }) {
    // Using declare global block or similar for window properties is recommended
    // For brevity, keeping window.map as is.
    const map = (window as any).map;
    const mapboxPoiSource = (window as any).mapboxPoiSource;

    window.layerRegistry.addSource(POI_SOURCE_ID, {
      type: 'vector',
      url: mapboxPoiSource, // Your new Mapbox tileset URL
      promoteId: 'id' // Assuming 'id' is the unique identifier property
    })

    const poiIconExpression = [
      'match',
      ['get', 'type'],
      'campground', 'puck_icon_campground',
      'hotel', 'puck_icon_hotel',
      'hostel', 'puck_icon_hostel',
      'bnb', 'puck_icon_bnb',
      'motel', 'puck_icon_motel',
      'osmCampsite', 'puck_icon_osmCampsite',
      'osmcampsite', 'puck_icon_osmcampsite',
      'osm_campsite', 'puck_icon_osmcampsite',
      'osm-campsite', 'puck_icon_osmcampsite',
      'osmCampsites', 'puck_icon_osmCampsite',
      'shelter', 'puck_icon_shelter',
      'pub', 'puck_icon_pub',
      'fastfood', 'puck_icon_fastfood',
      'fast_food', 'puck_icon_fastfood',
      'fast-food', 'puck_icon_fastfood',
      'viewpoint', 'puck_icon_viewpoint',
      'transportStation', 'puck_icon_transportStation',
      'transportStations', 'puck_icon_transportStation',
      'transport_station', 'puck_icon_transport_station',
      'transport-station', 'puck_icon_transport_station',
      'shop', 'puck_icon_shop',
      'town', 'puck_icon_town',
      'history', 'puck_icon_history',
      'busStop', 'puck_icon_busStop',
      'busStops', 'puck_icon_busStop',
      'bus_stop', 'puck_icon_bus_stop',
      'bus-stop', 'puck_icon_bus_stop',
      'bus-stops', 'puck_icon_bus_stop',
      'postOffice', 'puck_icon_postOffice',
      'post_office', 'puck_icon_post_office',
      'post-office', 'puck_icon_post_office',
      'peak', 'puck_icon_peak',
      'peaks', 'puck_icon_peaks',
      'pharmacy', 'puck_icon_pharmacy',
      'water', 'puck_icon_water',
      'healthcare', 'puck_icon_healthcare',
      'restaurant', 'puck_icon_restaurant',
      'other', 'puck_icon_other',
      'cave', 'puck_icon_cave',
      'coffee', 'puck_icon_coffee',
      'evCharging', 'puck_icon_evCharging',
      'ev_charging', 'puck_icon_ev_charging',
      'ev-charging', 'puck_icon_ev_charging',
      'gate', 'puck_icon_gate',
      'information', 'puck_icon_information',
      'junction', 'puck_icon_junction',
      'parking', 'puck_icon_parking',
      'picnic', 'puck_icon_picnic',
      'playground', 'puck_icon_playground',
      'restroom', 'puck_icon_restroom',
      'storage', 'puck_icon_storage',
      'swimming', 'puck_icon_swimming',
      'wifi', 'puck_icon_wifi',
      'wildlife', 'puck_icon_wildlife',
      'waterfall', 'puck_icon_waterfall',
      'warning', 'puck_icon_warning',
      ['concat', 'puck_icon_', ['get', 'type']]
    ];

    window.layerRegistry.addLayer({
      id: POI_LAYER_ID,
      type: 'symbol',
      source: POI_SOURCE_ID,
      'source-layer': 'poi', // The name of the layer within your tileset
      minzoom: 9,
      filter: ['in', ['get', 'type'], ''], // Initially show nothing
      layout: {
        'icon-image': poiIconExpression,
        'icon-size': 0.25,
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
      },
    }, MapLayerCategory.Top)
  },

  /**
   * Updates the filter on the single POI layer based on activatedPoiTypes.
   * Expands each activated type into all its known variants so the filter
   * matches regardless of the casing convention used in the vector tileset.
   */
  updatePoiFilter({ state }) {
    const map = (window as any).map;
    if (!map || !map.getLayer(POI_LAYER_ID)) {
      console.warn("POI layer not available for filtering.");
      return;
    }

    if (state.activatedPoiTypes.length === 0) {
      map.setFilter(POI_LAYER_ID, ['boolean', false]);
      return;
    }

    // Expand each activated primary type into all known tileset variants
    const allVariants: string[] = [];
    for (const activeType of state.activatedPoiTypes) {
      const poiUtil = state.poiList.find(p => p.mapboxFilterType() === activeType);
      if (poiUtil) {
        allVariants.push(...poiUtil.mapboxFilterTypes());
      } else {
        allVariants.push(activeType);
      }
    }

    map.setFilter(POI_LAYER_ID, ['in', ['get', 'type'], ['literal', allVariants]]);
  },

  /**
   * Initialize activated POIs
   */
  initActivePois({ state, commit, dispatch }) {
    dispatch('addPoiSourceAndLayer');

    const storedActivatedTypes: string[] = JSON.parse(
      localStorage.getItem("activatedPoiTypes") || "[]"
    );
    const isKnownType = (type: string) =>
      state.poiList.some((poiUtil) => poiUtil.mapboxFilterType() === type);
    const validActivatedTypes = storedActivatedTypes.filter(isKnownType);

    // A shared POI link is not a layer-selection link, even though both ride on
    // `?poi=`, so it is handled by its own action rather than by the passed-POI
    // branch below — see openSharedPoiFromUrl.
    if (sharedPoiFromUrl(window.location.href)) {
      commit("SET_ACTIVATED_POI_TYPES", validActivatedTypes);
      dispatch("openSharedPoiFromUrl");
      return;
    }

    const passedPoiUtils = POIUtility.checkForPassedPOIs();
    if (passedPoiUtils.length > 0) {
      const passedTypes = passedPoiUtils
        .map((poi) => poi.mapboxFilterType())
        .filter(isKnownType);

      commit("SET_ACTIVATED_POI_TYPES", passedTypes);
    } else {
      commit("SET_ACTIVATED_POI_TYPES", validActivatedTypes);
    }

    dispatch("updatePoiFilter");
  },

  /**
   * Opens the POI a `?poi_id=…&poi_class=…` link names, flying to it first.
   *
   * The counterpart to the dialog's Share action. `poi_class` rides along
   * because amenities and accommodations have overlapping primary keys, so the
   * id alone would not say which record to fetch — it is exactly what the map's
   * own click handler passes to `showPoi`.
   *
   * Runs once per page load: re-adding the layers on a style change must not
   * reopen a sheet the walker has already closed.
   */
  openSharedPoiFromUrl({ state, commit, dispatch }) {
    if (state.sharedPoiHandled) return;

    const sharedPoi = sharedPoiFromUrl(window.location.href);
    if (!sharedPoi) return;

    commit("SET_SHARED_POI_HANDLED", true);

    // The POI's own layer, switched on so the pin is still there once the sheet
    // is dismissed. A union, not a replacement: `?poi` doubles as the map's
    // layer-selection parameter, so overwriting it would silently switch off
    // every other layer the walker had chosen just because someone sent them a
    // waterfall.
    const merged = sharedPoiSelection(sharedPoi, state.activatedPoiTypes);
    if (merged) {
      const known = merged.filter((type) =>
        state.poiList.some((poiUtil) => poiUtil.mapboxFilterType() === type)
      );
      commit("SET_ACTIVATED_POI_TYPES", known);
      dispatch("updatePoiFilter");
    }

    const map = (window as any).map;
    const center = sharedPoiCenter(sharedPoi);
    if (center && map) {
      map.flyTo({ center, zoom: sharedPoiZoom(map.getZoom()) });
    }

    // `window.showPoi` is registered by the dialog's own mounted hook, which
    // normally runs long before the map fires `map-loaded`. Normally — so this
    // waits a few frames rather than silently dropping the link on the ordering
    // it cannot guarantee. Bounded, so a page with no dialog mounted gives up
    // instead of polling forever.
    let attemptsLeft = SHARED_POI_SHOW_ATTEMPTS;
    const show = () => {
      const showPoi = (window as any).showPoi;
      if (showPoi) {
        // No seed: the link carries an id and a class but none of the POI's
        // detail, so the dialog fetches it rather than rendering a half-empty
        // card first.
        showPoi(sharedPoi.klass, sharedPoi.id);
        return;
      }
      if (--attemptsLeft > 0) setTimeout(show, SHARED_POI_SHOW_RETRY_MS);
    };
    show();
  },

  addAllPois({ state, commit, dispatch }) {
    const allPoiTypes = state.poiList.map(poi => poi.mapboxFilterType());
    commit("SET_ACTIVATED_POI_TYPES", allPoiTypes);
    dispatch("updatePoiFilter");
  },

  addParkPois({ state, commit, dispatch }) {
    const parkPoiTypes = POIUtility.allParkUtils().map(poi => poi.mapboxFilterType());
    const currentTypes = new Set(state.activatedPoiTypes);
    parkPoiTypes.forEach(type => currentTypes.add(type));
    commit("SET_ACTIVATED_POI_TYPES", Array.from(currentTypes));
    dispatch("updatePoiFilter");
  },

  refreshActivePois({ dispatch }) {
    console.log('Refreshing POI filter explicitly.');
    dispatch('addPoiSourceAndLayer');
    dispatch("updatePoiFilter");
  },

  /**
   * Activate the click handler for the single POI layer.
   */
  activateClickHandler({ state, rootState }) {
    const map = (window as any).map;
    const showPoi = (window as any).showPoi;
    const mapboxgl = (window as any).mapboxgl; // Assuming mapboxgl is global

    if (!map) return;

    const clickHandler = (e: any) => { // Use mapboxgl.MapMouseEvent if available
      if (!e.features || e.features.length === 0) return;

      // Access rootState directly (ensure RootState type includes planTrail)
      if (rootState.planTrail && rootState.planTrail.planMode) {
        return;
      }
      if (isClickOnWaypointHelper(map.queryRenderedFeatures(e.point))) {
        console.log("Clicked on a non-POI waypoint, ignoring POI popup.");
        return;
      }

      const feature = e.features[0];
      const properties = feature.properties;
      const poiType = properties.type;
      const poiId = properties.id;

      const poiUtility = state.poiList.find(p => p.mapboxFilterTypes().includes(poiType));

      if (poiUtility && showPoi) {
        const coords =
          feature.geometry && feature.geometry.type === 'Point'
            ? (feature.geometry.coordinates as [number, number])
            : [e.lngLat.lng, e.lngLat.lat];
        const seed = {
          id: poiId,
          type: poiType || null,
          name: properties.name || null,
          lonlat: coords,
        };
        showPoi(poiUtility.databaseClassName(), poiId, seed);
      } else {
        console.warn(`POIUtility or window.showPoi not found for type: ${poiType}, id: ${poiId}`);
        if (feature.geometry && feature.geometry.type === 'Point' && mapboxgl) {
          const coordinates = feature.geometry.coordinates.slice() as [number, number];
          const description = Object.entries(properties)
            .map(([k, v]) => `<strong>${k}</strong>: ${v}`)
            .join('<br>');

          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(description || 'No details available')
            .addTo(map);
        }
      }
    };

    // Ensure handler is removed before adding
    map.off('click', POI_LAYER_ID, clickHandler);
    map.on('click', POI_LAYER_ID, clickHandler);

    // Hover effects
    const mouseEnterHandler = () => { map.getCanvas().style.cursor = 'pointer'; };
    const mouseLeaveHandler = () => { map.getCanvas().style.cursor = ''; };

    map.off('mouseenter', POI_LAYER_ID, mouseEnterHandler);
    map.on('mouseenter', POI_LAYER_ID, mouseEnterHandler);

    map.off('mouseleave', POI_LAYER_ID, mouseLeaveHandler);
    map.on('mouseleave', POI_LAYER_ID, mouseLeaveHandler);
  },

  // Consider replacing window.bus with Vue 3 provide/inject or mitt if possible
  // Keeping window.bus logic for direct conversion
  viewCreated({ dispatch }) {

    const mapLoadedHandler = () => {
      dispatch("initActivePois");
      dispatch("activateClickHandler");
    };
    const mapStyleLoadedHandler = () => {
      dispatch("addPoiSourceAndLayer");
      dispatch("updatePoiFilter");
      dispatch("activateClickHandler");
      // Also here, not only from initActivePois: `map-loaded` fires once, and
      // this module's handler for it is registered by a component that may be
      // created after the map has already fired it — in which case a shared-POI
      // link did nothing at all. This event fires reliably, and the action's own
      // once-per-load guard makes the second call a no-op.
      dispatch("openSharedPoiFromUrl");
    };

    // Store handlers to remove them later
    (window as any)._mapDataPoiMapLoadedHandler = mapLoadedHandler;
    (window as any)._mapDataPoiMapStyleLoadedHandler = mapStyleLoadedHandler;

    emitter.on('map-loaded', mapLoadedHandler);
    emitter.on('map-style-loaded', mapStyleLoadedHandler);
  },

  beforeViewDestroyed() {
    const map = (window as any).map;

    // Remove bus listeners using stored handlers
    if ((window as any)._mapDataPoiMapLoadedHandler) {
      emitter.off('map-loaded', (window as any)._mapDataPoiMapLoadedHandler)
    }
    if ((window as any)._mapDataPoiMapStyleLoadedHandler) {
      emitter.off('map-style-loaded', (window as any)._mapDataPoiMapStyleLoadedHandler)
    }
    // Clean up map listeners (assuming handlers were defined within activateClickHandler)
    // Note: Directly removing anonymous functions added with .on() isn't possible without references.
    // The current activateClickHandler removes previous listeners before adding new ones,
    // which mitigates duplicate listeners but doesn't fully clean up on destroy without handler references.
    // For a full cleanup, store handler references outside the action or refactor listener management.
    if (map) {
      // These might not remove the handlers if they were anonymous functions added in activateClickHandler
      // A better approach is needed if precise cleanup is critical (e.g., storing named functions).
      map.off('click', POI_LAYER_ID);
      map.off('mouseenter', POI_LAYER_ID);
      map.off('mouseleave', POI_LAYER_ID);
    }
  },

  clearAllPoi({ commit, dispatch }) {
    commit("REMOTE_ALL_POI_TYPES");
    dispatch("updatePoiFilter");
  },

  /**
   * Toggle a POI type's active state and update the map filter.
   */
  togglePoi({ state, commit, dispatch }, poiUtil: POIUtility) {
    const poiType = poiUtil.mapboxFilterType();
    if (state.activatedPoiTypes.includes(poiType)) {
      commit("REMOVE_ACTIVATED_POI_TYPE", poiType);
    } else {
      commit("ADD_ACTIVATED_POI_TYPE", poiType);
    }
    dispatch("updatePoiFilter");
  },
};

// -- Getters (remain the same)
const getters: GetterTree<BaseMapDataPOIsState, RootState> = {
  isPoiActive: (state) => (poiUtil: POIUtility): boolean => {
    const poiType = poiUtil.mapboxFilterType();
    return state.activatedPoiTypes.includes(poiType);
  },
  getAvailablePois: (state): POIUtility[] => {
    return state.poiList;
  },
  getActivePoiTypes: (state): string[] => {
    return state.activatedPoiTypes;
  }
};

// -- Export the module as a plain object conforming to the Module interface
// Removed: export default class MapDataPOIsModule implements Module<MapDataPOIsState, any> { ... }
const baseMapDataPOIsModule: Module<BaseMapDataPOIsState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default baseMapDataPOIsModule;
